import {Grid} from '@mui/material'
import {useCompany} from 'src/contexts/CompanyContext'
import useClients from 'src/hooks/api/client/useClients'
import useDashboardCameras from 'src/hooks/api/dashboard/useDashboardCameras'
import useDashboardCountByCamera from 'src/hooks/api/dashboard/useDashboardCountByCamera'
import useDashboardEventsByStatus from 'src/hooks/api/dashboard/useDashboardEventsByStatus'
import useDashboardObjectsCount from 'src/hooks/api/dashboard/useDashboardObjectsCount'
import AntennaIcon from 'src/images/AntennaIcon'
import CameraIcon from 'src/images/CameraIcon'
import ClockErrorIcon from 'src/images/ClockErrorIcon'
import DesactivatedIcon from 'src/images/DesactivatedIcon'
import OffIcon from 'src/images/OffIcon'
import DashboardColumnChartsCard from '../components/ColumnChartCard'
import DashboardCountCard from '../components/CountCard'
import DashboardStackChart from '../components/StackChart'
import DashboardTableRowCard from '../components/TableRowCard'
import {capitalizeText, DEFAULT_COLORS, translateObjectName} from './functions'

type Props = {
  filter: {cameraIds: Array<number>; clientIds: Array<number>; start: string; end: string; eventStatuses: Array<string>}
  isFacialEnabled: boolean
  isLprEnabled: boolean
}

export default function EventsDashboard({filter}: Props) {
  const {selectedCompanyId} = useCompany()
  const {data: clients} = useClients({selectedCompanies: [selectedCompanyId], includes: ['cameras']})

  const {
    data: cameraData,
    isRefetching: isRefreshingCameraData,
    isLoading: isLoadingCameraData,
  } = useDashboardCameras({filter: {clientIds: filter.clientIds, cameraIds: filter.cameraIds, companyId: selectedCompanyId}})
  const {
    data: cameraCountClientAggregationData,
    isRefetching: isRefetchingCameraCountClientData,
    isLoading: isLoadingCameraCountClientData,
  } = useDashboardCountByCamera({
    filter: {companyId: selectedCompanyId, clientIds: filter.clientIds, cameraIds: filter.cameraIds, status: filter.eventStatuses},
    aggregateBy: 'client',
    start: filter.start,
    end: filter.end,
  })
  const {
    data: cameraCountCameraAggregationData,
    isRefetching: isRefetchingCameraCountCameraData,
    isLoading: isLoadingCameraCountCameraData,
  } = useDashboardCountByCamera({
    filter: {companyId: selectedCompanyId, clientIds: filter.clientIds, cameraIds: filter.cameraIds, status: filter.eventStatuses},
    aggregateBy: 'camera',
    start: filter.start,
    end: filter.end,
  })
  const {
    data: objectsCountData,
    isRefetching: isRefetchingObjectsCountData,
    isLoading: isLoadingObjectsCountData,
  } = useDashboardObjectsCount({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds, status: filter.eventStatuses},
    start: filter.start,
    end: filter.end,
    enabled: selectedCompanyId !== 0,
  })
  const {
    data: eventsByHour,
    isRefetching: isRefetchingEventsByHour,
    isLoading: isLoadingEventsByHour,
  } = useDashboardEventsByStatus({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds, status: ['TODOS']},
    start: filter.start,
    end: filter.end,
  })
  const {
    data: eventsByStatus,
    isRefetching: isRefetchingEventsByStatus,
    isLoading: isLoadingEventsByStatus,
  } = useDashboardEventsByStatus({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds, status: filter.eventStatuses},
    start: filter.start,
    end: filter.end,
  })

  return (
    <Grid container gap={'40px'} padding={'0 20px'}>
      {/* row */}
      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={4} lg={2.4}>
          <DashboardCountCard
            title={'Total de Dispositivos'}
            description='dispositivos'
            value={(cameraData?.offline ?? 0) + (cameraData?.online ?? 0)}
            loading={isLoadingCameraData}
            refetching={isRefreshingCameraData}
            icon={<CameraIcon />}
          />
        </Grid>
        <Grid item container xs={4} lg={2.4}>
          <DashboardCountCard
            title={'Dispositivos Armados'}
            description='dispositivos'
            value={cameraData?.statuses.find((s) => s.status === 'ARMADO')?.count}
            loading={isLoadingCameraData}
            refetching={isRefreshingCameraData}
            icon={<AntennaIcon />}
          />
        </Grid>
        <Grid item container xs={4} lg={2.4}>
          <DashboardCountCard
            title={'Dispositivos Desarmados'}
            description='dispositivos'
            value={cameraData?.statuses.find((s) => s.status === 'DESARMADO')?.count}
            loading={isLoadingCameraData}
            refetching={isRefreshingCameraData}
            icon={<OffIcon />}
          />
        </Grid>
        <Grid item container xs={6} lg={2.4}>
          <DashboardCountCard
            title={'Dispositivos Fora do Horário'}
            description='dispositivos'
            value={cameraData?.statuses.find((s) => s.status === 'FORA DO HORÁRIO')?.count}
            loading={isLoadingCameraData}
            refetching={isRefreshingCameraData}
            icon={<ClockErrorIcon />}
          />
        </Grid>
        <Grid item container xs={6} lg={2.4}>
          <DashboardCountCard
            title={'Dispositivos Desabilitados'}
            description='dispositivos'
            value={cameraData?.offline}
            loading={isLoadingCameraData}
            refetching={isRefreshingCameraData}
            icon={<DesactivatedIcon />}
          />
        </Grid>
      </Grid>

      {/* row */}
      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={6} lg={6} gap={'32px'}>
          <DashboardTableRowCard
            title={'Clientes com mais eventos'}
            loading={isLoadingCameraCountClientData}
            refetching={isRefetchingCameraCountClientData}
            height={'412px'}
            data={
              cameraCountClientAggregationData?.slice(0, 5).map((c) => ({
                name: `${c.clientId} - ${clients?.data.data.entities.find((client) => client.id === c.clientId)?.name}`,
                value: c.data,
              })) ?? []
            }
          />
          <DashboardColumnChartsCard
            title='Visão geral de Eventos'
            loading={isLoadingObjectsCountData}
            refetching={isRefetchingObjectsCountData}
            data={[{name: '', data: Object.values(objectsCountData ?? {})}]}
            labelsXaxis={Object.keys(objectsCountData ?? {}).map((key) => translateObjectName(key))}
          />
        </Grid>
        <Grid item container xs={6} lg={6}>
          <DashboardTableRowCard
            title={'Dispositivos com mais eventos'}
            loading={isLoadingCameraCountCameraData}
            refetching={isRefetchingCameraCountCameraData}
            height={'747px'}
            data={
              cameraCountCameraAggregationData?.slice(0, 10).map((c) => ({
                name: `${c.cameraId} - ${clients?.data.data.entities.flatMap((client) => client.cameras).find((camera) => camera.id === c.cameraId)?.name ?? 'DESCONHECIDO'}`,
                value: c.data,
              })) ?? []
            }
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={12} lg={6}>
          <DashboardStackChart
            title='Eventos'
            data={
              eventsByHour?.result.map((value, index) => ({
                data: value.data,
                name: capitalizeText(value.name),
                color: DEFAULT_COLORS[index],
              })) ?? []
            }
            isLoading={isLoadingEventsByHour}
            isRefetching={isRefetchingEventsByHour}
            labelsXaxis={eventsByHour?.categories}
          />
        </Grid>
        <Grid item container xs={12} lg={6}>
          <DashboardStackChart
            title='Eventos por status'
            data={
              eventsByStatus?.result.map((value, index) => ({
                data: value.data,
                name: capitalizeText(value.name),
                color: DEFAULT_COLORS[index],
              })) ?? []
            }
            isLoading={isLoadingEventsByStatus}
            isRefetching={isRefetchingEventsByStatus}
            labelsXaxis={eventsByStatus?.categories}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
